import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tabs, TabItem } from "@/components/Tabs";
import { SettingsAccountTab } from "@/pages/Settings/Account";
import { SettingsAppearanceTab } from "@/pages/Settings/Appearance";
import { SettingsSecurityTab } from "@/pages/Settings/Security";
import { Icon } from "@/components/Icon";

type TabKey = "account" | "appearance" | "security";

export const Settings: React.FC = () => {
    const { t } = useTranslation("global");
    const { tab } = useParams<{ tab?: string }>();
    const navigate = useNavigate();

    // Support English URLs: /settings/account, /settings/appearance, /settings/security
    const activeTab: TabKey =
        tab === "appearance" || tab === "security" ? tab : "account";

    const handleTabChange = (newTab: TabKey): void => {
        if (newTab === "account") {
            navigate("/settings/account");
        } else {
            navigate(`/settings/${newTab}`);
        }
    };

    const tabItems: TabItem<TabKey>[] = [
        {
            id: "account",
            label: t("settings.tabs.account"),
            icon: <Icon value="user" size={16} />,
        },
        {
            id: "appearance",
            label: t("settings.tabs.appearance"),
            icon: <Icon value="sun" size={16} />,
        },
        {
            id: "security",
            label: t("settings.tabs.security"),
            icon: <Icon value="key" size={16} />,
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in w-full min-w-0">
            {/* Main Tabs Navigation (English URLs synced) */}
            <div className="flex w-full justify-center">
                <Tabs<TabKey>
                    tabs={tabItems}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                    activeBgClass="bg-primary"
                    activeTextClass="text-white"
                    centered
                />
            </div>

            {activeTab === "account" && <SettingsAccountTab />}
            {activeTab === "appearance" && <SettingsAppearanceTab />}
            {activeTab === "security" && <SettingsSecurityTab />}
        </div>
    );
};
